import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { api, unwrap } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import { PermissionGate } from '@/auth/PermissionGate';
import { useCan } from '@/auth/queries';
import { Campo } from '@/components/Campo';
import { Cargando, Encabezado, ErrorCarga, Vacio } from '@/components/Estados';
import { Modal } from '@/components/Modal';
import { Paginador } from '@/components/Paginador';
import { SubirImagen } from '@/components/SubirImagen';
import { useOpcionesLicencia } from '@/features/alumnos/queries';
import { useFiltrosUrl } from '@/hooks/useFiltrosUrl';
import { env } from '@/lib/env';
import { fechaLarga, mxn } from '@/lib/formato';
import { cn } from '@/lib/utils';

import { ESTADO_PROMO, useCupones, type Cupon, type CuponBody } from './queries';

const CLAVES = ['q', 'membershipId', 'status'] as const;

function FormCupon({ actual, onListo }: { actual: Cupon | null; onListo: () => void }) {
  const qc = useQueryClient();
  const opciones = useOpcionesLicencia();
  const [f, setF] = useState<CuponBody>(
    actual
      ? {
          membershipId: actual.membershipId,
          code: actual.code,
          name: actual.name,
          price: actual.price,
          finalPrice: actual.finalPrice,
          startsOn: actual.startsOn,
          endsOn: actual.endsOn,
          usageLimit: actual.usageLimit,
          isPublic: actual.isPublic,
          image: actual.image,
        }
      : { membershipId: 0, code: '', name: '', finalPrice: '', usageLimit: 0, isPublic: false },
  );
  const poner = (c: Partial<CuponBody>) => setF((v) => ({ ...v, ...c }));
  const licencia = opciones.data?.find((o) => o.id === f.membershipId);
  const guardar = useMutation({
    mutationFn: () =>
      actual
        ? unwrap(
            api.PATCH('/api/v1/admin/coupons/{id}', {
              params: { path: { id: String(actual.id) } },
              body: f,
            }),
          )
        : unwrap(api.POST('/api/v1/admin/coupons', { body: f })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tienda', 'cupones'] });
      toast.success(actual ? 'Cupón guardado.' : 'Cupón creado.');
      onListo();
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        guardar.mutate();
      }}
    >
      <Campo etiqueta="Licencia">
        <select
          className="input-vidrio"
          value={f.membershipId || ''}
          required
          onChange={(e) => poner({ membershipId: Number(e.target.value) })}
        >
          <option value="">Elige una licencia…</option>
          {opciones.data?.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name} · {mxn(o.price)}
            </option>
          ))}
        </select>
      </Campo>
      <div className="grid gap-3 sm:grid-cols-2">
        <Campo etiqueta="Código" ayuda="Se guarda en mayúsculas.">
          <input
            className="input-vidrio font-mono uppercase"
            value={f.code}
            required
            minLength={3}
            maxLength={30}
            onChange={(e) => poner({ code: e.target.value })}
          />
        </Campo>
        <Campo etiqueta="Nombre">
          <input
            className="input-vidrio"
            value={f.name}
            required
            minLength={2}
            maxLength={50}
            onChange={(e) => poner({ name: e.target.value })}
          />
        </Campo>
        <Campo
          etiqueta="Precio con cupón (MXN)"
          ayuda={licencia ? `Precio de la licencia: ${mxn(licencia.price)}` : undefined}
        >
          <input
            className="input-vidrio"
            inputMode="decimal"
            value={f.finalPrice}
            required
            onChange={(e) => poner({ finalPrice: e.target.value })}
          />
        </Campo>
        <Campo etiqueta="Límite de usos" ayuda="0 = sin límite">
          <input
            type="number"
            min={0}
            className="input-vidrio"
            value={f.usageLimit ?? 0}
            onChange={(e) => poner({ usageLimit: Number(e.target.value) })}
          />
        </Campo>
        <Campo etiqueta="Vigente desde">
          <input
            type="date"
            className="input-vidrio"
            value={f.startsOn ?? ''}
            onChange={(e) => poner({ startsOn: e.target.value || null })}
          />
        </Campo>
        <Campo etiqueta="Vigente hasta">
          <input
            type="date"
            className="input-vidrio"
            value={f.endsOn ?? ''}
            min={f.startsOn ?? undefined}
            onChange={(e) => poner({ endsOn: e.target.value || null })}
          />
        </Campo>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          className="h-4 w-4 accent-[var(--color-azul-600)]"
          checked={f.isPublic ?? false}
          onChange={(e) => poner({ isPublic: e.target.checked })}
        />
        Promoción pública (visible en la tienda)
      </label>
      <SubirImagen
        destino="cupon"
        valor={f.image}
        urlDe={(n) => `${env.cdnUrl}/src/content/${n}`}
        onCambio={(image) => poner({ image })}
      />
      <div className="flex justify-end gap-2">
        <button type="button" className="btn-cs btn-contorno" onClick={onListo}>
          Cancelar
        </button>
        <button type="submit" className="btn-cs btn-primario" disabled={guardar.isPending}>
          Guardar
        </button>
      </div>
    </form>
  );
}

function Cupones() {
  const { filtros, cambiar, query } = useFiltrosUrl(CLAVES);
  const editable = useCan('tienda', 'editar');
  const opciones = useOpcionesLicencia();
  const qc = useQueryClient();
  const [editando, setEditando] = useState<Cupon | 'nuevo' | null>(null);
  const q = useCupones({ ...query, page: filtros.page, pageSize: 50 });
  const terminar = useMutation({
    mutationFn: (id: number) =>
      unwrap(api.POST('/api/v1/admin/coupons/{id}/end', { params: { path: { id: String(id) } } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tienda', 'cupones'] });
      toast.success('Cupón terminado.');
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });
  return (
    <>
      <Encabezado
        titulo="Cupones y promociones"
        descripcion="Precio especial por código para una licencia, con vigencia y límite de usos."
        acciones={
          editable && (
            <button
              type="button"
              className="btn-cs btn-primario"
              onClick={() => setEditando('nuevo')}
            >
              <Plus size={16} aria-hidden /> Nuevo cupón
            </button>
          )
        }
      />
      <div className="mb-4 flex flex-wrap gap-3">
        <input
          className="input-vidrio max-w-xs"
          placeholder="Código o nombre"
          defaultValue={filtros.q}
          onKeyDown={(e) => e.key === 'Enter' && cambiar({ q: e.currentTarget.value.trim() })}
          onBlur={(e) => cambiar({ q: e.target.value.trim() })}
          aria-label="Buscar"
        />
        <select
          className="input-vidrio max-w-64"
          value={filtros.membershipId}
          onChange={(e) => cambiar({ membershipId: e.target.value })}
          aria-label="Licencia"
        >
          <option value="">Todas las licencias</option>
          {opciones.data?.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
        <select
          className="input-vidrio max-w-48"
          value={filtros.status}
          onChange={(e) => cambiar({ status: e.target.value })}
          aria-label="Vigencia"
        >
          <option value="">Cualquier vigencia</option>
          <option value="current">Vigentes</option>
          <option value="scheduled">Programados</option>
          <option value="expired">Vencidos</option>
        </select>
      </div>
      {q.isPending && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {q.data?.data.length === 0 && <Vacio titulo="Sin cupones con esos filtros" />}
      {q.data && q.data.data.length > 0 && (
        <div className="panel-vidrio overflow-x-auto p-0">
          <table className="tabla-cs w-full text-sm">
            <thead>
              <tr>
                <th>Código</th>
                <th>Nombre</th>
                <th>Licencia</th>
                <th>Precio</th>
                <th>Vigencia</th>
                <th>Usos</th>
                <th>Estado</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {q.data.data.map((c) => {
                const [texto, clase] = ESTADO_PROMO[c.status] ?? ['', ''];
                return (
                  <tr key={c.id}>
                    <td className="font-mono font-bold">{c.code}</td>
                    <td>
                      {c.name}
                      {c.isPublic && <span className="insignia ml-1">Pública</span>}
                    </td>
                    <td>{c.membershipName}</td>
                    <td className="whitespace-nowrap tabular">
                      {mxn(c.finalPrice)}{' '}
                      <span className="text-xs text-texto-3 line-through">{mxn(c.price)}</span>
                    </td>
                    <td className="whitespace-nowrap">
                      {c.startsOn ? fechaLarga(c.startsOn) : '—'} –{' '}
                      {c.endsOn ? fechaLarga(c.endsOn) : 'sin fin'}
                    </td>
                    <td className="tabular">
                      {c.uses}
                      {c.usageLimit > 0 && ` / ${c.usageLimit}`}
                    </td>
                    <td>
                      <span className={cn('insignia', clase)}>{texto}</span>
                    </td>
                    <td className="space-x-2 text-right whitespace-nowrap">
                      {editable && (
                        <>
                          <button
                            type="button"
                            className="btn-cs btn-contorno px-2 py-1 text-xs"
                            onClick={() => setEditando(c)}
                          >
                            Editar
                          </button>
                          {c.status !== 'expired' && (
                            <button
                              type="button"
                              className="btn-cs btn-peligro px-2 py-1 text-xs"
                              disabled={terminar.isPending}
                              onClick={() => terminar.mutate(c.id)}
                            >
                              Terminar
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {q.data && (
        <Paginador
          page={q.data.page}
          pageSize={q.data.pageSize}
          total={q.data.total}
          onPage={(page) => cambiar({ page })}
        />
      )}
      <Modal
        abierto={editando !== null}
        onCerrar={() => setEditando(null)}
        titulo={editando === 'nuevo' ? 'Nuevo cupón' : `Editar ${editando?.code ?? ''}`}
        ancho="max-w-2xl"
      >
        {editando !== null && (
          <FormCupon
            key={editando === 'nuevo' ? 'nuevo' : editando.id}
            actual={editando === 'nuevo' ? null : editando}
            onListo={() => setEditando(null)}
          />
        )}
      </Modal>
    </>
  );
}

export function CuponesPage() {
  return (
    <PermissionGate seccion="tienda">
      <Cupones />
    </PermissionGate>
  );
}
